import React, {
  ReactNode,
} from 'react';
import {
  Button,
} from '@populateui/ui';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  PCCDashboardProgramDetailDTO,
} from 'dtos';
import {
  PCCDashboardProgramStatus,
} from 'dtos/pCCDashboardProgramStatus';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import PCCAlertList from 'pages/Dashboard/pages/Dashboard/PCCOverview/alerts/PCCAlertList';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';

type Props = {
  children: ReactNode;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
  patient: PCCPatientActionContext;
  program: PCCProgram;
  programDetail?: PCCDashboardProgramDetailDTO;
};

export default function PatientProgramContent({
  children,
  isError,
  isFetching,
  onRetry,
  patient,
  program,
  programDetail,
}: Props) {
  const isInitialLoading = isFetching && isNil(programDetail);
  const isOptedOut = Boolean(programDetail?.isOptedOut);
  const isNotEligible = !isOptedOut
    && programDetail?.status === PCCDashboardProgramStatus.NotEligible;
  const isNotEnrolled = !isOptedOut
    && programDetail?.status === PCCDashboardProgramStatus.Eligible;
  const isUnavailable = !isFetching
    && !isError
    && !isOptedOut
    && !isNotEnrolled
    && (isNil(programDetail) || isNil(programDetail.status));
  const showContent = !isInitialLoading
    && !isError
    && !isUnavailable
    && !isOptedOut
    && !isNotEligible
    && !isNotEnrolled;
  let statusMessage = '';

  if (isNotEligible) {
    statusMessage = `Not eligible for ${program}.`;
  } else if (isOptedOut) {
    statusMessage = `Opted out of ${program}.`;
  } else if (isNotEnrolled) {
    statusMessage = `Not enrolled in ${program}.`;
  } else if (isUnavailable) {
    statusMessage = `${program} information is unavailable.`;
  }

  const showStatusMessage = notEmptyString(statusMessage);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {isInitialLoading && (
        <div className="flex flex-1 items-center justify-center bg-gray-50 p-4 text-sm text-gray-500">
          Loading {program} information...
        </div>
      )}
      {!isInitialLoading && isError && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-gray-50 p-4 text-sm text-red-600">
          <span>Unable to load {program} eligibility information.</span>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            size="small"
            onClick={onRetry}
          >
            Retry
          </Button>
        </div>
      )}
      {showStatusMessage && (
        <div className="flex flex-col gap-3 bg-gray-50 p-4">
          {(programDetail?.alerts ?? []).length > 0 ? (
            <PCCAlertList
              alerts={programDetail?.alerts}
              patient={patient}
            />
          ) : (
            <p className="text-left text-sm text-gray-600">{statusMessage}</p>
          )}
        </div>
      )}
      {showContent && children}
    </div>
  );
}
